/**
 * Report enforced CSP violations from real browsers (sip-nq4).
 *
 * The problem this solves: the payment flow depends on third-party origins being allowed by
 * our policy, and if Klarna moves a host, the failure is completely silent. A blocked CDN
 * leaves `klarna_enabled` false, so Step2 just greys the tile out; nothing reaches the access
 * log, the backend, or the user. That is how 0791e97 took both payment methods down until two
 * customer tickets surfaced it the next morning (sip-9vh).
 *
 * `tools/check_payment_csp.py` and the smoke suite both check the policy against a *declaration*
 * of the origins we believe are needed. Neither can notice Klarna moving to a host nobody has
 * declared. This can: the browser that gets blocked tells us.
 *
 * **Why this works even though report-uri does not.** `report-uri` is ignored inside a `<meta>`
 * tag, and our `script-src`/`connect-src`/`frame-src` have to live in the meta because only the
 * build knows the hydration script's hash -- so those directives cannot be reported by the
 * platform's own mechanism. The `securitypolicyviolation` DOM event has no such limitation.
 * Measured 2026-08-06: a meta policy with `img-src 'self'` blocking x.klarnacdn.net delivered
 * `{effectiveDirective: 'img-src', blockedURI: 'https://x.klarnacdn.net/blocked.png'}` to a JS
 * listener. This closes a gap AGENTS.md records as structural.
 */

import { scrubUrlQuery } from './sentry-scrub.js';

/**
 * Directives whose every source in our policy is 'self' (plus data:/blob:), checked against
 * svelte.config.js on 2026-08-17: img-src, font-src and style-src carry no remote origin at all.
 *
 * A violation of one of these is therefore third-party code by construction -- it can never be
 * "a host of ours we forgot to declare", because we serve none of them from a host. That is what
 * makes suppressing them safe, and it is the only thing that does: the shape test below cannot
 * help, because translate.google.com is as real a remote https origin as js.klarna.com.
 *
 * Measured before this list existed (sip-kfa): 10 of 17 unresolved GlitchTip issues, 59% of the
 * list, were Google Translate and a cashback extension tripping exactly these three.
 *
 * A DENYLIST OF THREE, NOT AN ALLOWLIST OF SCRIPT/CONNECT/FRAME, and the difference matters. An
 * allowlist goes quiet the day a directive nobody anticipated starts mattering -- a Klarna
 * worker, a media element -- which is the failure sip-3mb already paid for once with a list of
 * noise markers. This way the unanticipated case still reports.
 *
 * IF YOU ADD A REMOTE ORIGIN to img-src, font-src or style-src in svelte.config.js, delete it
 * from this list in the same commit, or a missing declaration of ours becomes invisible.
 */
const SELF_ONLY_DIRECTIVES = ['img-src', 'font-src', 'style-src'];

/** Extension schemes. These inject into the page and trip CSP doing it; not our policy's fault. */
const EXTENSION_SCHEMES = ['chrome-extension:', 'moz-extension:', 'safari-extension:', 'safari-web-extension:', 'webkit-masked-url:'];

export function directiveOf(violation) {
	// effectiveDirective is the modern field; Safari populated only violatedDirective for years.
	return violation?.effectiveDirective || violation?.violatedDirective || 'unknown';
}

/**
 * The origin a violation is about, which is what we group on.
 *
 * Grouping on the full URL would open a new GlitchTip issue for every cache-busted SDK path,
 * turning one broken host into an unbounded stream of issues.
 */
export function originOf(blockedURI) {
	try {
		return new URL(blockedURI).origin;
	} catch {
		return blockedURI;
	}
}

/**
 * Is this violation worth an event?
 *
 * Two rules, and they answer different questions.
 *
 * By SHAPE -- a real remote origin was blocked -- rather than a list of noise markers to exclude.
 * That direction is deliberate: sentry-filter.js was rewritten the same way after a marker list
 * lost to in-app browsers once already (sip-3mb), and there is no reason the next browser
 * extension reuses a scheme we happen to know about.
 *
 * By DIRECTIVE, because shape alone was not enough (sip-kfa). Third-party code injected into our
 * page trips img-src, font-src and style-src from origins that pass the shape test perfectly.
 * See SELF_ONLY_DIRECTIVES above for why dropping exactly those three loses no signal of ours.
 */
export function shouldReportViolation(violation) {
	// disposition 'report' means a report-only policy matched and nothing was actually blocked.
	if (violation?.disposition && violation.disposition !== 'enforce') return false;

	// Browsers report style-src-elem and style-src-attr, never a bare style-src, so the suffix
	// has to come off before the comparison -- and script-src-elem is the same shape on the side
	// that must keep reporting.
	if (SELF_ONLY_DIRECTIVES.includes(directiveOf(violation).replace(/-(elem|attr)$/, ''))) return false;

	const blocked = violation?.blockedURI;
	if (typeof blocked !== 'string' || !blocked) return false;

	// 'inline', 'eval', 'data', 'blob' and friends are overwhelmingly extensions and injected
	// page scripts. Our own inline script is allowed by hash, and if that ever broke the app
	// would white-screen -- a failure nobody needs a report to notice.
	if (!/^https?:\/\//i.test(blocked)) return false;

	return !EXTENSION_SCHEMES.some((scheme) => blocked.toLowerCase().startsWith(scheme));
}

export function violationKey(violation) {
	return `${directiveOf(violation)}|${originOf(violation?.blockedURI ?? '')}`;
}

/**
 * Wire the listener.
 *
 * @param {(message: string, context: Record<string, unknown>) => void} capture
 * @param {{addEventListener: Function}} [target] defaults to document
 */
export function installCspReporter(capture, target = typeof document === 'undefined' ? null : document) {
	if (!target?.addEventListener) return;

	const seen = new Set();

	target.addEventListener('securitypolicyviolation', (violation) => {
		// This runs on every page load of the payment flow. It must never be the reason a
		// customer cannot pay, so nothing in here is allowed to escape.
		try {
			if (!shouldReportViolation(violation)) return;

			const key = violationKey(violation);
			if (seen.has(key)) return;
			seen.add(key);

			const directive = directiveOf(violation);
			const origin = originOf(violation.blockedURI);

			capture(`CSP blocked ${origin} (${directive})`, {
				directive,
				blockedURI: violation.blockedURI,
				// Both of these are the page's own URL, and on the PayPal confirmation page that
				// URL is the payer's name, email address and postal address (sip-9mr). The page
				// is the diagnosis; its query string is somebody's data.
				sourceFile: scrubUrlQuery(violation.sourceFile),
				lineNumber: violation.lineNumber,
				documentURI: scrubUrlQuery(violation.documentURI)
			});
		} catch {
			// Deliberately swallowed -- see above.
		}
	});
}
